import { addDays, format, isSameMonth } from 'date-fns';

import {
  FillStatusEnum,
  JobDashboardStatusEnum,
  JobStatusEnum,
  VisibilityStatusEnum,
} from '@/types/graphql';

export const ALL_JOB_STATUSES: JobStatusEnum[] = [
  JobStatusEnum.CANCELLED,
  JobStatusEnum.COMPLETED,
  JobStatusEnum.IN_PROGRESS,
  JobStatusEnum.NEEDS_APPROVAL,
  JobStatusEnum.UPCOMING,
];

/** Statuses covered by "Select All" / the default "All active" placeholder.
 *  Cancelled is intentionally excluded — it's a separate opt-in checkbox so
 *  cancelled jobs only appear when the user explicitly asks for them. */
export const ACTIVE_JOB_STATUSES: JobStatusEnum[] = [
  JobStatusEnum.COMPLETED,
  JobStatusEnum.IN_PROGRESS,
  JobStatusEnum.NEEDS_APPROVAL,
  JobStatusEnum.UPCOMING,
];

export const ALL_FILL_RATES = ['FILLED', 'UNFILLED'] as const;
export type FillRateValue = (typeof ALL_FILL_RATES)[number];

export const VISIBILITY_TYPES: VisibilityStatusEnum[] = [
  VisibilityStatusEnum.LTA,
  VisibilityStatusEnum.W2,
  VisibilityStatusEnum.PUBLIC,
  VisibilityStatusEnum.PRIVATE,
];

/** UI-level type values. The "ALL" enum variant is omitted — it isn't a
 *  user-selectable type, it's the absence of a filter. */
export type TypeValue =
  | Exclude<VisibilityStatusEnum, VisibilityStatusEnum.ALL>
  | 'NO_SHOW';

export const ALL_TYPES: TypeValue[] = [
  VisibilityStatusEnum.LTA,
  VisibilityStatusEnum.W2,
  'NO_SHOW',
  VisibilityStatusEnum.PUBLIC,
  VisibilityStatusEnum.PRIVATE,
];

export const JOB_STATUS_LABELS: Record<JobStatusEnum, string> = {
  [JobStatusEnum.CANCELLED]: 'Cancelled',
  [JobStatusEnum.COMPLETED]: 'Completed',
  [JobStatusEnum.IN_PROGRESS]: 'In progress',
  [JobStatusEnum.NEEDS_APPROVAL]: 'Needs approval',
  [JobStatusEnum.UPCOMING]: 'Upcoming',
};

export const FILL_RATE_LABELS: Record<FillRateValue, string> = {
  FILLED: 'Filled',
  UNFILLED: 'Unfilled',
};

export const TYPE_LABELS: Record<TypeValue, string> = {
  [VisibilityStatusEnum.LTA]: 'Long-term',
  [VisibilityStatusEnum.W2]: 'W2',
  [VisibilityStatusEnum.PUBLIC]: 'Public',
  [VisibilityStatusEnum.PRIVATE]: 'Private',
  NO_SHOW: 'No show',
};

/** "May 25 – 31" when same month, "May 28 – Jun 3" across months. */
export const formatWeekLabel = (startDate: Date): string => {
  const end = addDays(startDate, 6);
  if (isSameMonth(startDate, end)) {
    return `${format(startDate, 'MMM d')} – ${format(end, 'd')}`;
  }
  return `${format(startDate, 'MMM d')} – ${format(end, 'MMM d')}`;
};

/** Chip label for multi-select pills: shows first selected + "+N" remainder. */
export const buildMultiChipLabel = (
  selectedLabels: string[],
): { label: string; more: string } => {
  if (selectedLabels.length === 0) return { label: '', more: '' };
  if (selectedLabels.length === 1)
    return { label: selectedLabels[0], more: '' };
  return {
    label: selectedLabels[0],
    more: `+${selectedLabels.length - 1}`,
  };
};

/**
 * Reduce a multi-checkbox "fill rate" UI to the single backend enum.
 * Both/none selected → ALL (no filter); just one → that one.
 */
export const deriveFillStatus = (
  selected: Set<FillRateValue>,
): FillStatusEnum => {
  if (selected.size === 0 || selected.size === ALL_FILL_RATES.length) {
    return FillStatusEnum.ALL;
  }
  return selected.has('FILLED')
    ? FillStatusEnum.FILLED
    : FillStatusEnum.UNFILLED;
};

/**
 * Split the unified "Types" multi-select back into the two backend fields:
 *  - dashboardVisibility: LTA / W2 / PUBLIC / PRIVATE
 *  - jobDashboardStatus:  NO_SHOW (other dashboard statuses are managed by StatusPill)
 *
 * Returns `undefined` for either list when nothing is selected, so the GraphQL
 * filter input omits the key entirely.
 */
export const splitTypeSelection = (
  selected: Set<TypeValue>,
): {
  visibility: VisibilityStatusEnum[] | undefined;
  includesNoShow: boolean;
} => {
  const visibility = VISIBILITY_TYPES.filter((v) =>
    selected.has(v as TypeValue),
  );
  return {
    visibility: visibility.length > 0 ? visibility : undefined,
    includesNoShow: selected.has('NO_SHOW'),
  };
};

const isUiVisibility = (
  v: VisibilityStatusEnum,
): v is Exclude<VisibilityStatusEnum, VisibilityStatusEnum.ALL> =>
  v !== VisibilityStatusEnum.ALL;

/** Inverse of splitTypeSelection — used when hydrating from Redux/URL. */
export const joinTypeSelection = (
  visibility: VisibilityStatusEnum[] | null | undefined,
  jobDashboardStatus: JobDashboardStatusEnum[] | null | undefined,
): Set<TypeValue> => {
  const result = new Set<TypeValue>();
  (visibility || []).forEach((v) => {
    if (isUiVisibility(v)) result.add(v);
  });
  if ((jobDashboardStatus || []).includes(JobDashboardStatusEnum.NO_SHOW)) {
    result.add('NO_SHOW');
  }
  return result;
};
